import type { TFunction } from "i18next";
import type { StepDisplayState } from "../../../api/threads/liveMessages";
import { valueText } from "../../threads/status/valueText";
import type { GraphRun, NodeRun } from "../model/graphRun";

/**
 * What a waiting step waits for, in the Run's own words (X-2, W-22: the Run view's composer and
 * person step say the same): the run graph's page words them, the card only places them.
 * `input`: whom the person step C names waits for; `null` while none waits.
 */
export interface RunWaits {
  readonly slot: string;
  readonly approval: string;
  readonly offline: string;
  readonly input: string | null;
}

/**
 * What a card says of the Run in place of its key fact (WD-4, U-3): what a waiting step waits
 * for, in the state C shows it; else a step's first output as C summed it up (`valueText`); a
 * group that stopped part way, how many of its steps are done. Nothing for a step that made
 * nothing yet.
 */
export function runCardLines(run: GraphRun, waits: RunWaits, t: TFunction): ReadonlyMap<string, string> {
  return new Map([...run].flatMap(([nodeId, facts]) => {
    const line = runLine(facts, waits, t);
    return line === null ? [] : [[nodeId, line] as const];
  }));
}

function runLine({ state, attempt, group }: NodeRun, waits: RunWaits, t: TFunction): string | null {
  if (group !== null) {
    return group.settled > 0 && group.settled < group.steps ? t("threads.runGraph.groupSettled", { settled: group.settled, steps: group.steps }) : null;
  }
  const waitingFor = WAITING_FOR[state];
  const wait = waitingFor === undefined ? null : waits[waitingFor];
  if (wait !== null) return wait;
  const made = attempt?.outputs?.[0];
  return made === undefined ? null : valueText(made, t);
}

/** The states C shows for a step that waits, and which of the Run's waits each one is. */
const WAITING_FOR: Partial<Readonly<Record<StepDisplayState, keyof RunWaits>>> = { slot: "slot", approval: "approval", offline: "offline", input: "input" };
